import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { TrendingUp, Users, Target, MapPin, FileText, Package } from 'lucide-react';

const EP3D_LOGO = "https://customer-assets.emergentagent.com/job_sales-force-hub-3/artifacts/8yq7y2ex_Logotipo_eng3d_2-removebg-preview%20%281%29.gif";

export default function LoginPage() {
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Bem-vindo de volta!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao entrar');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('As palavras-passe não coincidem');
      return;
    }
    setIsLoading(true);
    try {
      await register(registerForm.name, registerForm.email, registerForm.password, 'admin');
      toast.success('Conta criada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registar');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-4">
            <img 
              src={EP3D_LOGO} 
              alt="EP3D" 
              className="h-20 w-auto"
            />
            <div>
              <h1 className="text-4xl font-bold font-[Manrope] text-amber-400">VENDAS</h1>
              <p className="text-slate-400 text-lg">Sistema de Gestão Comercial</p>
            </div>
          </div>
          
          <div className="space-y-8">
            <div className="grid grid-cols-2 gap-6">
              <div className="p-5 bg-slate-800/50 rounded-xl border border-slate-700">
                <TrendingUp className="h-8 w-8 mb-3 text-amber-400" />
                <h3 className="font-semibold text-lg">Pipeline Visual</h3>
                <p className="text-slate-400 text-sm mt-1">Acompanhe todas as oportunidades</p>
              </div>
              <div className="p-5 bg-slate-800/50 rounded-xl border border-slate-700">
                <Users className="h-8 w-8 mb-3 text-blue-400" />
                <h3 className="font-semibold text-lg">Gestão de Clientes</h3>
                <p className="text-slate-400 text-sm mt-1">Base de dados centralizada</p>
              </div>
              <div className="p-5 bg-slate-800/50 rounded-xl border border-slate-700">
                <MapPin className="h-8 w-8 mb-3 text-emerald-400" />
                <h3 className="font-semibold text-lg">Rotas e Check-in</h3>
                <p className="text-slate-400 text-sm mt-1">Planificação geolocalizada</p>
              </div>
              <div className="p-5 bg-slate-800/50 rounded-xl border border-slate-700">
                <FileText className="h-8 w-8 mb-3 text-purple-400" />
                <h3 className="font-semibold text-lg">Encomendas</h3>
                <p className="text-slate-400 text-sm mt-1">Notas de encomenda por cliente</p>
              </div>
            </div>
          </div>
          
          <p className="text-slate-500 text-sm">
            © 2024 Engineering Project 3D. Todos os direitos reservados.
          </p>
        </div>
      </div>
      
      {/* Right Side - Auth Forms */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <img 
              src={EP3D_LOGO} 
              alt="EP3D" 
              className="h-16 w-auto mx-auto mb-2"
            />
            <h1 className="text-2xl font-bold text-amber-500 font-[Manrope]">VENDAS</h1>
          </div>
          
          <Card className="shadow-xl border-0">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-center font-[Manrope]">
                Aceder à conta
              </CardTitle>
              <CardDescription className="text-center">
                Entre ou crie uma nova conta para começar
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Entrar</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Registar</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        data-testid="login-email-input"
                        type="email"
                        placeholder="seu@email.com"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Palavra-passe</Label>
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                      data-testid="login-submit-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? 'A entrar...' : 'Entrar'}
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Nome</Label>
                      <Input
                        id="register-name"
                        data-testid="register-name-input"
                        type="text"
                        placeholder="O seu nome"
                        value={registerForm.name}
                        onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        data-testid="register-email-input"
                        type="email"
                        placeholder="seu@email.com"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Palavra-passe</Label>
                      <Input
                        id="register-password"
                        data-testid="register-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-confirm">Confirmar palavra-passe</Label>
                      <Input
                        id="register-confirm"
                        data-testid="register-confirm-input"
                        type="password"
                        placeholder="••••••••"
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                        required
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                      data-testid="register-submit-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? 'A criar conta...' : 'Criar conta (Admin)'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
